import Papa from 'papaparse';
import { CDMO_IMPORT_COLUMNS, ERROR_REPORT_COLUMNS } from './columns';
import { describeDuplicate, describeRowPosition, type ValidatedCdmoImportRow } from './validateRow';

export interface ErrorReportRow {
  rowNumber: number;
  raw: Record<string, string>;
  errors: string[];
  warnings: string[];
}

/** A report cell: which uploaded row it is about, then one message per line. */
const reportCell = (rowNumber: number, messages: readonly string[]) =>
  messages.length ? [`${describeRowPosition(rowNumber)}:`, ...messages.map((m) => `• ${m}`)].join('\n') : '';

/**
 * Rows that belong in the error report: every row that is not plainly Ready, plus any row the
 * import itself could not create (`importProblems`, keyed by row number), with that reason added.
 */
export function collectErrorReportRows(
  rows: readonly ValidatedCdmoImportRow[],
  importProblems: ReadonlyMap<number, string> = new Map()
): ErrorReportRow[] {
  return rows
    .filter((row) => row.state !== 'ready' || importProblems.has(row.rowNumber))
    .map((row) => {
      const duplicate = describeDuplicate(row);
      const problem = importProblems.get(row.rowNumber);
      return {
        rowNumber: row.rowNumber,
        raw: row.raw,
        errors: [...(duplicate ? [duplicate] : []), ...row.errors, ...(problem ? [problem] : [])],
        warnings: row.warnings,
      };
    });
}

/**
 * The error report: the uploaded rows exactly as they were, in the template's columns, followed by
 * `import_errors` and `import_warnings` — each naming the uploaded row it is about, then one message
 * per line. The researcher fixes the cells and
 * re-uploads the same file; the parser ignores the two extra columns.
 *
 * Starts with a byte-order mark so Excel opens it as UTF-8 (the data is full of em dashes); the
 * parser strips it again on re-upload.
 */
export function buildErrorReportCsv(rows: readonly ErrorReportRow[]): string {
  const csv = Papa.unparse(
    {
      fields: [...CDMO_IMPORT_COLUMNS, ...ERROR_REPORT_COLUMNS],
      data: rows.map((row) => [
        ...CDMO_IMPORT_COLUMNS.map((column) => row.raw[column] ?? ''),
        reportCell(row.rowNumber, row.errors),
        reportCell(row.rowNumber, row.warnings),
      ]),
    },
    { newline: '\r\n' }
  );
  return `\uFEFF${csv}\r\n`;
}

/** `WuXi.csv` → `WuXi_import_report.csv`. */
export function errorReportFileName(uploadedFileName: string): string {
  const base = uploadedFileName.replace(/\.csv$/i, '') || 'cdmo_import';
  return `${base}_import_report.csv`;
}
